import { useState } from 'react';
import {
  CTS_REVIEW_CADENCES, CTS_REVIEW_CADENCE_LABEL, CTS_REVIEW_LABEL_TEXT, CTS_ASSESSMENT_IN_PROGRESS_LABEL,
  CTS_REVIEW_OVERRIDE_RATIONALE_MAX, ctsReviewCadenceWithSource, type CtsReviewCadence,
} from '@grc/shared';
import { cn } from '@/lib/utils';
import { formatCalendarDate } from '@/lib/calendar-date';
import { CARD_SURFACE } from '@/components/compliance/card-surface';
import { useSetControlReviewCadence } from '@/api/hooks/useCtsReview';
import type { ControlHub } from '@/api/types/cts-workspace.types';

/**
 * CAPR S3 — THE CONTROL'S REVIEW SCHEDULE (UX-02, UX-10).
 *
 * ⚠ EVERYTHING HERE IS THE SERVER'S. The label, the next review date and the last approved date come
 * from the one resolver the list also reads; this component renders them and does no date arithmetic.
 *
 * ⚠ TWO FACTS, SIDE BY SIDE. Open work shows "Assessment in progress" NEXT TO the review label, never
 * instead of it — an in-progress control that is overdue still reads overdue.
 *
 * ⚠ THE CHANGE CONTROL IS AFFORDANCE, NOT AUTHORIZATION. It is offered only to the roles the server
 * accepts (risk manager / super admin); the server refuses everyone else whatever this renders.
 */
const TONE: Record<string, string> = {
  overdue: 'text-cert-expired-fg', due_soon: 'text-cert-warn-fg', current: 'text-cert-valid-fg',
  not_yet_assessed: 'text-text-faint italic', no_scheduled_review: 'text-text-secondary',
};
const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';
const DEFAULT_CHOICE = '';

function errorText(err: unknown): string {
  const e = err as { response?: { data?: { errors?: { message?: string }[] } }; message?: string };
  return e?.response?.data?.errors?.[0]?.message ?? e?.message ?? 'The review cadence could not be saved.';
}

export function ReviewSchedulePanel({ hub, canChange }: { hub: ControlHub; canChange: boolean }) {
  const r = hub.review;
  const set = useSetControlReviewCadence();
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState<string>(DEFAULT_CHOICE);
  const [rationale, setRationale] = useState('');

  const open = () => {
    setChoice(r.cadence_source === 'override' ? r.effective_cadence : DEFAULT_CHOICE);
    setRationale(r.override_rationale ?? '');
    set.reset();
    setEditing(true);
  };
  const toDefault = choice === DEFAULT_CHOICE;
  const blank = rationale.trim() === '';
  const save = () => set.mutate(
    toDefault
      ? { controlReference: hub.control.control_reference, cadence: null }
      : { controlReference: hub.control.control_reference, cadence: choice as CtsReviewCadence, rationale: rationale.trim() },
    { onSuccess: () => setEditing(false) },
  );

  const next = formatCalendarDate(r.next_review_date);

  return (
    <div className={cn(CARD_SURFACE, 'px-[16px] py-[12px]')} data-testid="review-schedule">
      <div className="flex flex-wrap items-start gap-x-[28px] gap-y-[10px]">
        <div>
          <span className={cn(EYEBROW, 'block')}>Review</span>
          <span className={cn('mt-[3px] block text-[13px] font-medium', TONE[r.review_label] ?? 'text-text-primary')}
            data-testid="review-label">
            {CTS_REVIEW_LABEL_TEXT[r.review_label] ?? r.review_label}
          </span>
          {r.in_progress && (
            <span className="block text-[11px] text-text-secondary" data-testid="review-in-progress">
              {CTS_ASSESSMENT_IN_PROGRESS_LABEL}
            </span>
          )}
        </div>
        <div>
          <span className={cn(EYEBROW, 'block')}>Next review</span>
          <span className="mt-[3px] block text-[13px] text-text-primary" data-testid="review-next">
            {next ?? <span className="italic text-text-faint">{r.review_label === 'no_scheduled_review' ? 'No scheduled review' : 'None yet'}</span>}
          </span>
        </div>
        {/* ⓘ The last approved date is not repeated here: the posture strip above shows it ("Last
            assessed"), from the same server field, and one fact belongs in one place. */}
        <div className="min-w-0 flex-1">
          <span className={cn(EYEBROW, 'block')}>Cadence</span>
          <span className="mt-[3px] flex flex-wrap items-baseline gap-[10px] text-[13px] text-text-primary">
            <span data-testid="review-cadence">{ctsReviewCadenceWithSource(r.effective_cadence, r.cadence_source)}</span>
            {canChange && !editing && (
              <button type="button" onClick={open} data-testid="review-change"
                className="text-[12px] font-medium text-accent-cyan hover:underline">
                Change
              </button>
            )}
          </span>
          {r.cadence_source === 'override' && r.override_rationale && (
            <span className="mt-[2px] block text-[11.5px] text-text-secondary" data-testid="review-rationale-shown">
              “{r.override_rationale}”{r.override_set_by_name ? ` — ${r.override_set_by_name}` : ''}
            </span>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-[12px] space-y-[8px] border-t border-border-subtle pt-[12px]" data-testid="review-editor">
          <label className="block text-[12px] text-text-secondary">
            Review cadence for this control
            <select value={choice} onChange={(e) => setChoice(e.target.value)} data-testid="review-cadence-select"
              className="mt-[4px] block w-[260px] rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-[13px] text-text-primary">
              <option value={DEFAULT_CHOICE}>
                Organization default ({CTS_REVIEW_CADENCE_LABEL[r.organization_cadence]})
              </option>
              {CTS_REVIEW_CADENCES.map((c) => <option key={c} value={c}>{CTS_REVIEW_CADENCE_LABEL[c]}</option>)}
            </select>
          </label>
          {!toDefault && (
            <label className="block text-[12px] text-text-secondary">
              Why does this control need a different cadence? (required)
              <textarea value={rationale} onChange={(e) => setRationale(e.target.value)} data-testid="review-rationale"
                maxLength={CTS_REVIEW_OVERRIDE_RATIONALE_MAX} rows={2}
                className="mt-[4px] block w-full max-w-[560px] rounded-md border border-border-subtle bg-bg-surface-2 px-3 py-2 text-[13px] text-text-primary" />
            </label>
          )}
          <p className="text-[11px] text-text-faint">
            Changing the cadence recalculates the next review date. It does not start, stop or change an assessment.
          </p>
          {set.isError && <p className="text-[12px] text-cert-expired-fg" role="alert">{errorText(set.error)}</p>}
          <div className="flex gap-[8px]">
            <button type="button" onClick={save} disabled={set.isPending || (!toDefault && blank)} data-testid="review-save"
              className="rounded-[7px] bg-accent-cyan px-[13px] py-[6px] text-[12.5px] font-medium text-white disabled:opacity-50">
              Save
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={set.isPending}
              className="rounded-[7px] border border-border-subtle px-[11px] py-[6px] text-[12px] text-text-secondary">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
